import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFetcher } from '@remix-run/react';

import { cn } from '@rocco/utils/cn';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';
import { Textarea } from '@rocco/components/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';

import type { LeadFormCopy } from '../types';
import {
    INDUSTRY_OPTIONS,
    STATE_OPTIONS,
    getCityOptions,
} from '../config/shared.config';
import { B2B_LEAD_API_ROUTE } from '~/routes/remix-api.b2b.lead';
import {
    LEAD_FORM_DEFAULT_VALUES,
    leadFormSchema,
    type LeadFormValues,
} from './lead-schema';

export interface LeadFormProps {
    copy: LeadFormCopy;
    /** `page` = embedded frosted card on the lead-form section; `modal` = inside the lead modal. */
    variant: 'page' | 'modal';
    className?: string;
    /** Called after a successful submission (e.g. to keep a modal open on success). */
    onSuccess?: () => void;
}

interface LeadActionResult {
    ok: boolean;
    errors?: Record<string, string[]>;
}

/**
 * Placeholder text per field for the page-variant rendering (Figma node
 * 521:27970).
 */
const PAGE_PLACEHOLDERS: Record<string, string> = {
    firstName: 'e.g. Paul',
    lastName: 'e.g. Smith',
    businessName: 'e.g. Atelier Studio',
    businessWebsite: 'https://',
    email: 'e.g. paul@studio.com',
    phone: 'e.g. (555) 123-4567',
    jobTitle: 'e.g. Principal Architect',
    industry: 'Select your industry',
    state: 'Select your state',
    city: 'Select your city',
    message: 'Please contact us to discuss opportunities with ArchiPro',
};

/**
 * Placeholder text per field for the modal-variant rendering (Figma node
 * 521:28038). Capitalised title-case for text fields, and the spec
 * long-form copy for the message textarea.
 */
const MODAL_PLACEHOLDERS: Record<string, string> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    businessName: 'Business Name',
    businessWebsite: 'Business Website',
    email: 'Business Email',
    phone: 'Business Phone',
    jobTitle: 'Job Title',
    industry: 'Select Industry',
    state: 'Select State',
    city: 'Select City',
    message: 'Please contact us to discuss opportunities with ArchiPro',
};

/**
 * The single B2B lead form ("Apply to Join"). Used both as the page-mode
 * section and inside the modal — `variant` only adjusts presentation. Submits
 * to {@link B2B_LEAD_API_ROUTE} via a fetcher so it works from either context
 * without a route navigation.
 */
export const LeadForm = ({
    copy,
    variant,
    className,
    onSuccess,
}: LeadFormProps) => {
    const fetcher = useFetcher<LeadActionResult>();
    const isSubmitting = fetcher.state !== 'idle';
    const isSuccess = fetcher.data?.ok === true;

    const form = useForm<LeadFormValues>({
        resolver: zodResolver(leadFormSchema),
        mode: 'onChange',
        defaultValues: LEAD_FORM_DEFAULT_VALUES,
    });

    const selectedState = form.watch('state');
    const cityOptions = getCityOptions(selectedState);
    const isPage = variant === 'page';

    // Clear the city whenever the state changes so a stale city isn't submitted.
    useEffect(() => {
        form.resetField('city', { defaultValue: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedState]);

    useEffect(() => {
        if (isSuccess) {
            form.reset(LEAD_FORM_DEFAULT_VALUES);
            onSuccess?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    const onSubmit = (values: LeadFormValues) => {
        fetcher.submit(
            { ...values, message: values.message ?? '' },
            { method: 'post', action: B2B_LEAD_API_ROUTE },
        );
    };

    if (isSuccess) {
        return (
            <div
                className={cn(
                    'flex flex-col items-start justify-center gap-3',
                    className,
                )}
                role="alert"
                aria-live="assertive"
            >
                <h3
                    className={cn(
                        'font-sans text-2xl',
                        isPage ? 'text-white' : 'text-current',
                    )}
                >
                    {copy.successHeading}
                </h3>
                <p className="text-base opacity-80">{copy.successBody}</p>
            </div>
        );
    }

    const placeholderFor = (name: string, modalFallback: string) =>
        isPage
            ? (PAGE_PLACEHOLDERS[name] ?? modalFallback)
            : (MODAL_PLACEHOLDERS[name] ?? modalFallback);

    const labelClass = isPage ? PAGE_LABEL_CLASS : MODAL_LABEL_CLASS;
    const inputClass = isPage ? PAGE_INPUT_CLASS : MODAL_INPUT_CLASS;
    const textareaClass = isPage ? PAGE_TEXTAREA_CLASS : MODAL_INPUT_CLASS;
    const selectTriggerClass = isPage
        ? PAGE_SELECT_TRIGGER_CLASS
        : MODAL_SELECT_TRIGGER_CLASS;
    const fieldGap = isPage
        ? 'gap-x-[1.66%] gap-y-[0.94vw] max-md:gap-y-5'
        : 'gap-x-[0.417vw] gap-y-[0.625vw] max-md:gap-y-3';

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn(
                    'grid grid-cols-1 md:grid-cols-2',
                    fieldGap,
                    className,
                )}
            >
                <TextField
                    name="firstName"
                    label="First name"
                    placeholder={placeholderFor('firstName', 'First name')}
                    autoComplete="given-name"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />
                <TextField
                    name="lastName"
                    label="Last name"
                    placeholder={placeholderFor('lastName', 'Last name')}
                    autoComplete="family-name"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />
                <TextField
                    name="jobTitle"
                    label="Job title"
                    placeholder={placeholderFor('jobTitle', 'Job title')}
                    autoComplete="organization-title"
                    className="md:col-span-2"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />

                <TextField
                    name="businessName"
                    label="Business name"
                    placeholder={placeholderFor(
                        'businessName',
                        'Business name',
                    )}
                    autoComplete="organization"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />
                <TextField
                    name="businessWebsite"
                    label="Business website"
                    placeholder={placeholderFor(
                        'businessWebsite',
                        'Business website',
                    )}
                    autoComplete="url"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />
                <TextField
                    name="email"
                    label="Business email"
                    placeholder={placeholderFor('email', 'Business email')}
                    autoComplete="email"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />
                <TextField
                    name="phone"
                    label="Business phone"
                    placeholder={placeholderFor('phone', 'Business phone')}
                    autoComplete="tel"
                    showAsterisk={false}
                    labelClass={labelClass}
                    inputClass={inputClass}
                    form={form}
                />

                <SelectField
                    name="industry"
                    label="Industry type"
                    placeholder={placeholderFor('industry', 'Select Industry')}
                    options={INDUSTRY_OPTIONS}
                    className="md:col-span-2"
                    labelClass={labelClass}
                    triggerClass={selectTriggerClass}
                    form={form}
                />
                <SelectField
                    name="state"
                    label="State"
                    placeholder={placeholderFor('state', 'Select State')}
                    options={STATE_OPTIONS}
                    labelClass={labelClass}
                    triggerClass={selectTriggerClass}
                    form={form}
                />
                <SelectField
                    name="city"
                    label="City"
                    placeholder={placeholderFor('city', 'Select City')}
                    options={cityOptions}
                    disabled={!selectedState}
                    labelClass={labelClass}
                    triggerClass={selectTriggerClass}
                    form={form}
                />

                <FormField
                    name="message"
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <FormItem className="md:col-span-2">
                            <FormLabel className={labelClass}>
                                Message
                            </FormLabel>
                            <FormControl>
                                <Textarea
                                    color="lightOutline"
                                    focusStyle="none"
                                    rows={isPage ? 3 : 4}
                                    placeholder={placeholderFor(
                                        'message',
                                        'Message',
                                    )}
                                    className={textareaClass}
                                    {...field}
                                />
                            </FormControl>
                            {fieldState.isTouched && (
                                <FormMessage
                                    variant="darker"
                                    className={FORM_MESSAGE_CLASS}
                                />
                            )}
                        </FormItem>
                    )}
                />

                <div
                    className={cn(
                        'flex justify-center md:col-span-2',
                        isPage
                            ? 'mt-[1.125rem] max-md:mt-4'
                            : 'mt-[0.938vw] max-md:mt-[1.125rem]',
                    )}
                >
                    <PillSubmit
                        variant={variant}
                        label={copy.submitLabel}
                        isLoading={isSubmitting}
                    />
                </div>
            </form>
        </Form>
    );
};

/* ---------------------------------------------------------------------------
 * Variant tokens
 *
 * Page variant — frosted card on top of a full-bleed image (Figma 521:27970):
 *   - uppercase Neue Haas Medium 12.485px white labels (tracking −0.25px)
 *   - 12.666px-rounded #fdfdfd inputs with Neue Haas Roman 14.696px placeholder
 *   - 28px vertical field gap; pill submit (#181818 inside #323232).
 *   Sizing uses vw against the 1920 frame so the card scales with the section.
 *
 * Modal variant — 950×900 dialog (Figma 521:28038) on a light grey #eaeaea
 * panel. Same visual scheme as page variant:
 *   - uppercase Neue Haas Medium 12.485px #161616 labels (tracking −0.25px)
 *   - 12.666px-rounded #fdfdfd inputs, placeholder Neue Haas Roman 14.696px
 *     black @ 30% opacity
 *   - 18px gap between fields, pill submit (#38383b inside #323232).
 *   Sizing also uses vw against the 1920 frame so the modal scales like the
 *   surrounding page (was previously in rem which left the modal stuck at
 *   full Figma size when the viewport narrowed). Mobile keeps the rem-based
 *   `max-md:` overrides — the drawer fills the phone viewport on its own.
 * ------------------------------------------------------------------------- */

// 12.485 px / 19.2 = 0.65 vw at the 1920 frame.
const MODAL_LABEL_CLASS = cn(
    'font-sans font-medium uppercase leading-[1.1] text-[#161616]',
    'text-[0.65vw] tracking-[-0.016em]',
    'max-md:text-[0.78rem]',
);
// 12.666 / 19.2 = 0.66 vw (radius + px), 15.481 / 19.2 = 0.806 vw (py),
// 14.696 / 19.2 = 0.766 vw (font).
//
// The desktop rules carry an explicit `md:` prefix so tailwind-merge dedupes
// them against Rocco's `text="default"` variant (which emits
// `text-[16px] leading-none md:text-base`). Without the prefix the
// unprefixed `text-[0.766vw]` loses to `md:text-base` at ≥ md viewports
// because the media-queried rule comes later in the cascade.
const MODAL_INPUT_CLASS = cn(
    'bg-[#fdfdfd] text-black placeholder:font-normal placeholder:tracking-[-0.02em] placeholder:text-black/30',
    'md:h-auto md:min-h-0 md:rounded-[0.66vw] md:px-[0.66vw] md:py-[0.806vw] md:text-[0.766vw] md:leading-[1.1]',
    'max-md:rounded-[0.792rem] max-md:px-[0.792rem] max-md:py-[0.968rem] max-md:text-[0.919rem]',
);
const MODAL_SELECT_TRIGGER_CLASS = cn(
    'w-full bg-[#fdfdfd] text-black',
    'data-[placeholder]:tracking-[-0.02em] data-[placeholder]:text-black',
    'md:h-auto md:min-h-0 md:rounded-[0.66vw] md:px-[0.66vw] md:py-[0.806vw] md:text-[0.766vw] md:leading-[1.1]',
    'max-md:rounded-[0.792rem] max-md:px-[0.792rem] max-md:py-[0.968rem] max-md:text-[0.919rem]',
);

const PAGE_LABEL_CLASS = cn(
    'font-sans font-medium uppercase leading-[1.1] text-white',
    'text-[0.65vw] tracking-[-0.02em]',
    'max-md:text-xs max-md:tracking-wide',
);
// Fixed-height inputs (Figma `517:24598` h=46.96 px) so each row matches the
// spec 68.96 px height and the form fits inside the 733 px card. `!h-[...]`
// overrides Rocco's default Input/Select h-9/h-10 that otherwise bloats rows.
//
// Desktop text size needs the `md:` prefix so tailwind-merge can dedupe it
// against Rocco's Input variant (which emits `md:text-base` = 16 px and
// otherwise wins over an unprefixed `text-[0.77vw]` at md+ because media-
// queried rules come later in the cascade). SelectTrigger doesn't emit a
// `md:` text class so it appears correct even without the prefix — adding
// it everywhere keeps inputs / textarea / selects visually in sync.
const PAGE_INPUT_CLASS = cn(
    'bg-[#fdfdfd] text-black placeholder:font-normal placeholder:tracking-[-0.02em] placeholder:text-black/40',
    'rounded-[0.66vw] px-[0.66vw] leading-[1.1] md:text-[0.77vw]',
    '!h-[2.446vw] !min-h-0 py-0',
    'max-md:!h-auto max-md:rounded-md max-md:px-3 max-md:py-3 max-md:text-base',
);
const PAGE_SELECT_TRIGGER_CLASS = cn(
    // Match Input/Textarea placeholder weight (black @ 40 % opacity) so
    // unfilled selects and unfilled inputs read identically inside the card.
    'w-full bg-[#fdfdfd] text-black data-[placeholder]:tracking-[-0.02em] data-[placeholder]:text-black/40',
    'rounded-[0.66vw] px-[0.66vw] leading-[1.1] md:text-[0.77vw]',
    '!h-[2.446vw] !min-h-0 py-0',
    'max-md:!h-auto max-md:rounded-md max-md:px-3 max-md:py-3 max-md:text-base',
);
// Textarea height pinned to Figma spec (80 px). h-[] overrides Rocco's
// `rows`-driven default, keeping the message field from pushing the submit
// pill outside the card.
const PAGE_TEXTAREA_CLASS = cn(
    'bg-[#fdfdfd] text-black placeholder:font-normal placeholder:tracking-[-0.02em] placeholder:text-black/40',
    'rounded-[0.66vw] px-[0.66vw] py-[0.81vw] leading-[1.1] md:text-[0.77vw]',
    '!h-[4.167vw] !min-h-0 resize-none',
    'max-md:!h-24 max-md:rounded-md max-md:px-3 max-md:py-3 max-md:text-base',
);

// Validation messages match the uppercase label font-size (0.65vw desktop /
// text-xs on mobile) instead of Rocco's default text-sm, so they stay subtle in
// the compact form. The alert icon scales with the text. Used by both variants —
// the page and modal labels are the same size.
const FORM_MESSAGE_CLASS = cn(
    'mt-[0.3vw] text-[0.65vw] leading-[1.2] [&_svg]:size-[0.7vw]',
    'max-md:mt-1 max-md:text-xs max-md:[&_svg]:size-3',
);

interface PillSubmitProps {
    label: string;
    isLoading?: boolean;
    variant: 'page' | 'modal';
}

/**
 * Pill submit button. Outer `#323232` wrapper carrying the drop-shadow with
 * an inner pill button:
 *  - `page` variant: inner `#181818`, sized in vw against the 1920 frame so
 *    it scales with the section card (Figma 521:28030).
 *  - `modal` variant: inner `#38383b` (lighter), now also sized in vw against
 *    the 1920 frame (was rem) so it scales with the modal at narrow desktop
 *    widths. Mobile drawer overrides via `max-md:` for tappable size.
 */
const PillSubmit = ({ label, isLoading, variant }: PillSubmitProps) => {
    if (variant === 'modal') {
        return (
            <button
                type="submit"
                disabled={isLoading}
                className={cn(
                    'group inline-flex items-center justify-center rounded-full',
                    // 45.362 px / 19.2 = 2.363 vw; 2 / 19.2 = 0.104 vw;
                    // shadow 4.25 / 12.74 px → 0.222 vw / 0.663 vw.
                    'h-[2.363vw] bg-[#323232] p-[0.104vw] shadow-[0_0.222vw_0.663vw_rgba(0,0,0,0.34)]',
                    'disabled:cursor-not-allowed disabled:opacity-70',
                    'max-md:h-[2.835rem] max-md:p-[0.125rem] max-md:shadow-[0_0.266rem_0.796rem_rgba(0,0,0,0.34)]',
                )}
            >
                <span
                    className={cn(
                        'inline-flex h-full items-center justify-center rounded-full bg-[#38383b] text-white',
                        // 34.515 / 19.2 = 1.798 vw; 9.861 / 19.2 = 0.513 vw;
                        // 15.778 / 19.2 = 0.822 vw.
                        'px-[1.798vw] py-[0.513vw] text-[0.822vw] leading-[1.3] tracking-[0.02em]',
                        'whitespace-nowrap font-sans font-medium',
                        'max-md:px-[2.157rem] max-md:py-[0.616rem] max-md:text-[0.986rem]',
                    )}
                >
                    {isLoading ? 'Submitting…' : label}
                </span>
            </button>
        );
    }
    return (
        <button
            type="submit"
            disabled={isLoading}
            className={cn(
                'group inline-flex items-center justify-center rounded-full',
                'bg-[#323232] p-[0.1vw] shadow-[0_0.22vw_0.66vw_rgba(0,0,0,0.34)]',
                'disabled:cursor-not-allowed disabled:opacity-70',
                'max-md:p-[2px] max-md:shadow-[0_3px_10px_rgba(0,0,0,0.34)]',
            )}
        >
            <span
                className={cn(
                    'inline-flex items-center justify-center rounded-full bg-[#181818] text-white',
                    'px-[1.8vw] py-[0.51vw] text-[0.82vw] leading-[1.3] tracking-[0.02em]',
                    'whitespace-nowrap font-sans font-medium',
                    'max-md:px-6 max-md:py-3 max-md:text-base',
                )}
            >
                {isLoading ? 'Submitting…' : label}
            </span>
        </button>
    );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
interface FieldProps {
    name: keyof LeadFormValues;
    label: string;
    placeholder: string;
    labelClass: string;
    className?: string;
    /** react-hook-form instance; typed loosely to keep the helper generic. */
    form: any;
}

interface TextFieldProps extends FieldProps {
    autoComplete?: string;
    showAsterisk?: boolean;
    inputClass: string;
}

const TextField = ({
    name,
    label,
    placeholder,
    labelClass,
    className,
    autoComplete,
    showAsterisk,
    inputClass,
    form,
}: TextFieldProps) => (
    <FormField
        name={name}
        control={form.control}
        render={({ field, fieldState }: any) => (
            <FormItem className={className}>
                <FormLabel className={labelClass}>{label}</FormLabel>
                <FormControl>
                    <Input
                        text="default"
                        color="lightOutline"
                        focus="none"
                        placeholder={
                            showAsterisk ? `${placeholder}*` : placeholder
                        }
                        autoComplete={autoComplete}
                        className={inputClass}
                        {...field}
                    />
                </FormControl>
                {fieldState.isTouched && (
                    <FormMessage
                        variant="darker"
                        className={FORM_MESSAGE_CLASS}
                    />
                )}
            </FormItem>
        )}
    />
);

interface SelectFieldProps extends FieldProps {
    options: { value: string; label: string }[];
    disabled?: boolean;
    triggerClass: string;
}

const SelectField = ({
    name,
    label,
    placeholder,
    options,
    labelClass,
    className,
    disabled,
    triggerClass,
    form,
}: SelectFieldProps) => (
    <FormField
        name={name}
        control={form.control}
        render={({ field, fieldState }: any) => (
            <FormItem className={className}>
                <FormLabel className={labelClass}>{label}</FormLabel>
                <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={disabled}
                >
                    <FormControl>
                        <SelectTrigger
                            color="lightOutline"
                            className={triggerClass}
                        >
                            <SelectValue placeholder={placeholder} />
                        </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                        {options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {fieldState.isTouched && (
                    <FormMessage
                        variant="darker"
                        className={FORM_MESSAGE_CLASS}
                    />
                )}
            </FormItem>
        )}
    />
);
/* eslint-enable @typescript-eslint/no-explicit-any */
